import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import classNames from 'classnames';
import lodash from 'lodash';

import type {
  ExtensionPlaceholder,
  ExtensionProps,
  RicosExtension,
  RicosExtensionConfig,
  RicosNodeExtension,
} from './../ricos-types';
import { Node_Type } from './../ricos-types';
import type { TiptapAdapterServices } from './../tiptap-editor/initializeTiptapAdapter';

import { createRicosFunctionalExtension } from './create-ricos-extension';
import styles from './placeholder-scss';

export const placeholder = createRicosFunctionalExtension({
  type: 'extension',
  groups: [],
  name: 'placeholder',
  reconfigure: (
    config: RicosExtensionConfig,
    _extensions: RicosExtension[],
    ricosProps: ExtensionProps,
    _settings: Record<string, unknown>,
    services: TiptapAdapterServices
  ) => ({
    ...config,
    addOptions() {
      return {
        placeholder: ricosProps.placeholder || '',
        t: services.t,
        extensionsPlaceholders: lodash.compact(
          _extensions
            .filter((ext): ext is RicosNodeExtension =>
              ['html-node', 'react-node', 'extension'].includes(ext.type)
            )
            .map((ext: RicosNodeExtension) => ext.placeholder)
        ),
      };
    },
  }),
  createExtensionConfig() {
    return {
      name: this.name,
      addProseMirrorPlugins() {
        return [
          new Plugin({
            key: new PluginKey('editor-placeholders'),
            props: {
              decorations: ({ doc, selection }) => {
                const active = this.editor.isEditable;

                if (!active) {
                  return;
                }
                const decorations: Decoration[] = [];

                const { firstChild } = doc;
                const isEditorEmpty =
                  doc.childCount === 1 &&
                  firstChild &&
                  firstChild.type.name === Node_Type.PARAGRAPH &&
                  firstChild.childCount === 0;

                if (isEditorEmpty) {
                  decorations.push(
                    Decoration.node(0, firstChild.nodeSize, {
                      class: 'is-editor-empty',
                      'data-placeholder': this.options.placeholder,
                    })
                  );
                }

                const placeHolders: ExtensionPlaceholder[] = this.options.extensionsPlaceholders;
                if (!placeHolders.length) {
                  return DecorationSet.create(doc, decorations);
                }

                doc.descendants((node, pos) => {
                  const isEmpty = !node.isLeaf && !node.childCount;
                  if (!isEmpty) {
                    return;
                  }

                  const { t } = this.options;

                  const { content, alwaysVisible, alignCenter } =
                    placeHolders
                      .sort(({ priority: a = 0 }, { priority: b = 0 }) => b - a)
                      .find(({ predicate }) => {
                        return predicate({
                          doc,
                          pos,
                          node,
                          selection,
                          editor: this.editor,
                        });
                      }) || {};

                  if (!content) {
                    return;
                  }

                  decorations.push(
                    Decoration.node(pos, pos + node.nodeSize, {
                      class: classNames('is-node-empty', {
                        [styles.alignCenter]: alignCenter,
                      }),

                      'data-placeholder': t?.(content) || content,
                      ...(alwaysVisible ? { 'data-placeholder-always-visible': '' } : {}),
                    })
                  );

                  return true;
                });

                return DecorationSet.create(doc, decorations);
              },
            },
          }),
        ];
      },
    };
  },
});
